import { log } from '@domgen/shared/src/utils/log';
import { FormKey } from '../../enums/formKey';
import { StepKey } from '../../enums/stepKey';
import { getCategoryFromApplianceCodeInCatalogue } from './getCategoryFromApplianceCodeInCatalogue';
import { StoreContext } from '../../models/store/context';

/**
 * @summary This function checks if the provided model number matches any of the appliance lookup results in the context. If a match is found, it then retrieves the category for that appliance from the catalogue data in the context and updates the form with the appliance details (super category and item category). The function returns true if an appliance was found in the lookup and false otherwise.
 *
 * @param {StoreContext} context - The current store context
 * @param {string | undefined} modelNumber - The model number to check
 * @param {ApplianceFormData | undefined} [form] - The form object to update
 * @returns {boolean} True if appliance was found in lookup, false otherwise
 */
export const modelMatchesLookupAndCatalogue = (context: StoreContext): boolean => {
  let foundInLookup = false;

  if (context.form.modelSerial?.modelNumber && context.applianceLookupResults) {
    log.debug({
      message: 'Model number found, attempting to find matched appliance',
      data: {
        appliances: JSON.stringify(context.applianceLookupResults),
        modelNumber: context.form.modelSerial.modelNumber,
      },
    });
    const matchedAppliance = context.applianceLookupResults.find(
      (a) => a.modelNumber === context.form.modelSerial?.modelNumber,
    );

    if (matchedAppliance && context.app.catalogue) {
      log.debug({
        message: 'Matched appliance found, retrieving category from catalogue',
        data: {
          matchedAppliance: JSON.stringify(matchedAppliance),
        },
      });
      const category = getCategoryFromApplianceCodeInCatalogue(
        matchedAppliance.itemTypeCode,
        context.app.catalogue,
      );

      if (category) {
        log.debug({
          message: 'Category found for matched appliance, updating form with appliance details',
          data: {
            category: JSON.stringify(category),
          },
        });
        if (context.form) {
          context.form[StepKey.APPLIANCE_DETAILS] = {
            [FormKey.SUPER_CATEGORY]: category.categoryCode,
            [FormKey.ITEM_CATEGORY]: matchedAppliance.itemTypeCode,
          };
        }
        foundInLookup = true;
      }
    }
  }

  return foundInLookup;
};
